// Secure Logger Utility
// Prevents sensitive data logging in production and filters sensitive information

const isProduction = process.env.NODE_ENV === 'production';
const isDevelopment = process.env.NODE_ENV === 'development';

// Sensitive data patterns to filter out
const SENSITIVE_PATTERNS = [
  /token/i,
  /password/i,
  /auth/i,
  /secret/i,
  /key/i,
  /credential/i,
  /bearer/i,
  /session/i,
  /deviceid/i
];

// Sensitive data replacement text
const REDACTED = '[REDACTED]';

/**
 * Sanitizes objects by removing or redacting sensitive data
 * @param {any} data - Data to sanitize
 * @returns {any} - Sanitized data
 */
const sanitizeData = (data) => {
  if (!data || typeof data !== 'object') {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(sanitizeData);
  }

  const sanitized = {};
  for (const [key, value] of Object.entries(data)) {
    const isSensitive = SENSITIVE_PATTERNS.some(pattern => pattern.test(key));
    
    if (isSensitive) {
      sanitized[key] = REDACTED;
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeData(value);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
};

/**
 * Secure logger that only logs in development and sanitizes sensitive data
 */
export const secureLogger = {
  log: (...args) => {
    if (isDevelopment) {
      console.log(...args.map(sanitizeData));
    }
  },
  
  error: (...args) => {
    if (isDevelopment) {
      console.error(...args.map(sanitizeData));
    } else {
      // In production, only log generic error messages
      console.error('An error occurred. Check application logs for details.');
    }
  },
  
  warn: (...args) => {
    if (isDevelopment) {
      console.warn(...args.map(sanitizeData));
    }
  },
  
  info: (...args) => {
    if (isDevelopment) {
      console.info(...args.map(sanitizeData));
    }
  },
  
  debug: (...args) => {
    if (isDevelopment) {
      console.debug(...args.map(sanitizeData));
    }
  }
};

/**
 * Performance-aware logger for tracking operations
 * @param {string} operation - Operation name
 * @param {Function} fn - Function to execute
 * @returns {Promise|any} - Result of the function
 */
export const withSecureLogging = async (operation, fn) => {
  const startTime = performance.now();
  
  try {
    secureLogger.info(`Starting operation: ${operation}`);
    const result = await fn();
    const endTime = performance.now();
    secureLogger.info(`Completed operation: ${operation} in ${(endTime - startTime).toFixed(2)}ms`);
    return result;
  } catch (error) {
    const endTime = performance.now();
    secureLogger.error(`Failed operation: ${operation} in ${(endTime - startTime).toFixed(2)}ms`, error);
    throw error;
  }
};

export default secureLogger; 